/**
 * ArtifactGallerySection — newest-first feed of AI-generated documents.
 *
 * Phase 1 of the "Project as ecosystem of work" track (June 7, 2026).
 * Backed by ``GET /projects/{id}/artifacts`` which filters
 * ``content_items`` to rows with ``origin_type IN ('document_fork',
 * 'document_create')`` scoped to ``project_id``. The gallery is the
 * persistent record of every editable file the agent has produced for
 * this project — separate from ``knowledge_canvases`` (auto-compiled
 * markdown wiki pages on semantic entities) which keep their own
 * dedicated hub.
 *
 * Why NOT re-use ``DocumentCardV2``:
 *   - That component takes a ``WorkspaceDocument`` with selection /
 *     pin / move / info hooks designed for the documents Hub. Adapting
 *     the lean ``ProjectArtifact`` shape into it would require ~30
 *     fake fields + a selection store the gallery doesn't need.
 *   - The gallery is read-only in Phase 1 (no select, no pin, no
 *     delete from here — the agent owns the lifecycle). A purpose-built
 *     ``ArtifactCard`` keeps the contract small + the DOM nodes lighter
 *     when the user pages through many artifacts.
 *
 * Layout: dense 2-column grid (1 col mobile) so the gallery scans
 * vertically without dwarfing siblings inside the accordion. Click on
 * a card deep-links to the originating conversation via
 * ``/workspace/conversations?focus=<conv>&msg=<...>`` (the standard
 * source-rail re-open path).
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import {
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Images,
  Presentation,
} from "lucide-react";
import type * as React from "react";

import type { ProjectArtifact } from "@ando/core/api";
import { AlertBanner, SurfaceSkeleton, cn } from "@ando/ui";

import { useProjectArtifacts } from "../../hooks/useProjects.js";
import { formatRelativeDateI18n } from "../../utils/relativeDate.js";

export interface ArtifactGallerySectionProps {
  readonly projectId: string;
}

/** Pick a glyph from the MIME type — degrades gracefully to a generic doc. */
function pickMimeIcon(mime: string | null): React.ComponentType<{
  className?: string;
}> {
  if (!mime) return FileText;
  if (mime.includes("spreadsheet") || mime.includes("excel")) return FileSpreadsheet;
  if (mime.includes("presentation") || mime.includes("powerpoint")) return Presentation;
  if (mime.startsWith("image/")) return Images;
  return FileText;
}

/** Human-readable file size — italian thousands, 1 decimal under MB. */
function formatBytes(size: number | null): string {
  if (size == null || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024).toLocaleString("it-IT")} KB`;
  }
  const mb = size / (1024 * 1024);
  return `${mb.toFixed(1).replace(".", ",")} MB`;
}

// Date formatting moved to ``utils/relativeDate`` — single source of
// truth shared with Memory / Tasks / Automations sections (code review
// D1 follow-up). Artifacts are past-facing: no "domani" recognition.
// The Lingui-aware variant resolves anchor labels via ``msg`` so
// ``oggi`` / ``ieri`` show up in the catalog. Created inside the
// component so it captures the current ``i18n`` instance.

const ArtifactCard: React.FC<{ readonly artifact: ProjectArtifact }> = ({
  artifact,
}) => {
  const { i18n } = useLingui();
  const Icon = pickMimeIcon(artifact.mime_type);
  const displayName =
    (artifact.filename ?? "").trim() || i18n._(msg`Documento senza nome`);
  const size = formatBytes(artifact.file_size);
  const date = formatRelativeDateI18n(artifact.created_at, i18n, {
    recogniseTomorrow: false,
  });
  const isFork = artifact.origin_type === "document_fork";

  // Deep-link to the originating conversation — opens the chat surface
  // with a ``focus`` param so the message that produced this artifact
  // scrolls into view. When no conversation is bound (rare — agent-
  // generated content sometimes lives outside a chat) we fall back to
  // an inert card.
  const conversationId = artifact.conversation_id;
  const linkable = conversationId !== null;

  const inner = (
    <>
      <span
        aria-hidden
        className={cn(
          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
          "bg-ando-orange-soft text-ando-orange",
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <div
          className="truncate font-[550] text-[13px] text-ink-1 leading-tight"
          title={displayName}
        >
          {displayName}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-ink-3">
          <span className="tabular-nums">{date}</span>
          {size ? (
            <>
              <span aria-hidden>·</span>
              <span className="tabular-nums">{size}</span>
            </>
          ) : null}
          <span aria-hidden>·</span>
          <span>
            {isFork ? <Trans>modificato</Trans> : <Trans>creato</Trans>}
          </span>
        </div>
      </span>
      {linkable ? (
        <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
      ) : null}
    </>
  );

  const baseClass = cn(
    "flex items-start gap-3 rounded-xl border border-hairline bg-white/70 p-3.5 shadow-sm",
    "transition-colors",
  );

  if (linkable) {
    return (
      <Link
        to="/workspace/conversations"
        search={{ focus: conversationId } as { focus: string }}
        aria-label={i18n._(msg`Apri conversazione che ha generato ${displayName}`)}
        className={cn(
          baseClass,
          "hover:bg-ink-1/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        )}
      >
        {inner}
      </Link>
    );
  }
  return <div className={baseClass}>{inner}</div>;
};

export const ArtifactGallerySection: React.FC<ArtifactGallerySectionProps> = ({
  projectId,
}) => {
  const { i18n } = useLingui();
  const query = useProjectArtifacts(projectId);

  if (query.isLoading) {
    return (
      <SurfaceSkeleton
        variant="card-grid"
        count={4}
        ariaLabel={i18n._(msg`Caricamento documenti generati`)}
      />
    );
  }

  if (query.isError) {
    return (
      <AlertBanner tone="error">
        <Trans>
          Impossibile caricare i documenti generati. Riprova fra poco.
        </Trans>
      </AlertBanner>
    );
  }

  const artifacts = query.data ?? [];

  if (artifacts.length === 0) {
    return (
      <div className="rounded-md border border-hairline border-dashed bg-card/40 px-4 py-6 text-center text-[12.5px] text-ink-2 leading-relaxed">
        <Trans>
          Nessun documento generato per ora. Chiedi all'agente di produrre un
          file (deck, F24, bozza email) nelle chat del progetto e lo troverai
          qui.
        </Trans>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[11.5px] text-ink-3">
        <Trans>
          <strong className="font-medium text-ink-2">{artifacts.length}</strong>{" "}
          documenti generati, più recenti per primi.
        </Trans>
      </div>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        {artifacts.map((a) => (
          <ArtifactCard key={a.id} artifact={a} />
        ))}
      </div>
    </div>
  );
};

ArtifactGallerySection.displayName = "ArtifactGallerySection";
